import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import api from '@/lib/api';
import { getSocket } from '@/lib/socket';
import type { JobStatus } from '@/types/job';

/**
 * Poll a background job's status and listen for Socket.IO completion/failure events.
 * Returns reactive state that updates as the job progresses.
 */
export function useJob<T = unknown>(queueName: string, jobId: string | null) {
  const completedRef = useRef(false);

  // Reset when jobId changes
  useEffect(() => {
    completedRef.current = false;
  }, [jobId]);

  const query = useQuery<JobStatus<T>>({
    queryKey: ['job', queueName, jobId],
    queryFn: async () => {
      const res = await api.get(`/jobs/${queueName}/${jobId}`);
      return res.data as JobStatus<T>;
    },
    enabled: !!jobId && !completedRef.current,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state === 'completed' || state === 'failed') {
        completedRef.current = true;
        return false;
      }
      return 2000; // Poll every 2s while active
    },
  });

  // Listen for Socket.IO events for faster updates
  useEffect(() => {
    if (!jobId) return;

    const socket = getSocket();

    const handleCompleted = (data: { jobId: string; queueName: string; result: T }) => {
      if (data.jobId === jobId && data.queueName === queueName) {
        completedRef.current = true;
        query.refetch();
      }
    };

    const handleFailed = (data: { jobId: string; queueName: string; error: string }) => {
      if (data.jobId === jobId && data.queueName === queueName) {
        completedRef.current = true;
        query.refetch();
      }
    };

    socket.on('job:completed', handleCompleted);
    socket.on('job:failed', handleFailed);

    return () => {
      socket.off('job:completed', handleCompleted);
      socket.off('job:failed', handleFailed);
    };
  }, [jobId, queueName, query]);

  const state = query.data?.state ?? (jobId ? 'waiting' : undefined);

  return {
    state,
    progress: query.data?.progress,
    result: query.data?.result,
    error: query.data?.failedReason,
    isLoading: state === 'waiting' || state === 'active',
    isComplete: state === 'completed',
    isFailed: state === 'failed',
    jobId,
  };
}

/**
 * Stage label map for analysis jobs
 */
export const ANALYSIS_STAGES: Record<string, string> = {
  parsing: 'Parsing trade history...',
  python_analysis: 'Analyzing patterns...',
  llm_rules: 'Generating rules...',
  saving: 'Creating draft...',
  complete: 'Analysis complete',
};

/**
 * Stage label map for backtest jobs
 */
export const BACKTEST_STAGES: Record<string, string> = {
  fetching_data: 'Fetching market data...',
  simulating: 'Running simulation...',
  complete: 'Backtest complete',
};

/**
 * Stage label map for suggestion jobs
 */
export const SUGGESTION_STAGES: Record<string, string> = {
  generating: 'Generating AI suggestions...',
  complete: 'Suggestions ready',
};
